import React, { useState } from 'react';
import Navbar from '../components/Navigation/Navbar';
import SciFiBackground from '../components/SciFiBackground/SciFiBackground';
import PlatformPageHeader from '../components/Common/PlatformPageHeader';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Avatar,
  Stack,
  Chip,
  Divider,
  IconButton,
  useTheme
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import HistoryIcon from '@mui/icons-material/History';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import InboxIcon from '@mui/icons-material/Inbox';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import BadgeIcon from '@mui/icons-material/Badge';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useTranslation } from 'react-i18next';

const Profile = () => {
  const { userProfile } = useAuth();
  const { t } = useTranslation();
  const theme = useTheme();
  const borderRadius = '25px';
  const compactRadius = '15px';
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: userProfile?.firstName || '',
    lastName: userProfile?.lastName || '',
    email: userProfile?.email || '',
    username: userProfile?.username || '',
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSave = () => {
    // Here you would typically make an API call to update the profile
    setIsEditing(false);
  };

  const ShortcutCard = ({ to, icon: Icon, title, subtitle, color }) => (
    <Paper
      component={Link}
      to={to}
      sx={{
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        textDecoration: 'none',
        color: 'inherit',
        background: theme.vitria?.input,
        backdropFilter: 'blur(10px)',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: compactRadius,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-5px)',
          background: theme.vitria?.surface,
          boxShadow: `0 8px 24px 0 ${color}40`,
          borderColor: color,
        },
      }}
    >
      <Box
        sx={{
          p: 1.5,
          borderRadius: '50%',
          bgcolor: `${color}20`,
          color: color,
          mb: 2,
        }}
      >
        <Icon fontSize="large" />
      </Box>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {subtitle}
      </Typography>
    </Paper>
  );

  return (
    <SciFiBackground>
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Navbar />
        <Box component="main" sx={{ flex: 1, display: 'flex', py: { xs: 6, md: 8 }, px: 2 }}>
          <Container maxWidth="lg">
          <PlatformPageHeader
            icon={PersonIcon}
            eyebrow={t('nav.profile', 'Account')}
            title={t('profile.dashboard')}
            subtitle={t('profile.subtitle')}
          />

          <Grid container spacing={4}>
            {/* Left Column: Avatar & Shortcuts */}
            <Grid item xs={12} md={4}>
              <Stack spacing={4}>
                {/* Avatar Card */}
                <Paper
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    background: theme.vitria?.input,
                    backdropFilter: 'blur(20px)',
                    borderRadius: borderRadius,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ position: 'relative', display: 'inline-block', mb: 3 }}>
                    <Avatar
                      sx={{
                        width: 120,
                        height: 120,
                        fontSize: '3rem',
                        fontWeight: 700,
                        bgcolor: 'primary.main',
                        boxShadow: '0 18px 45px rgba(118, 87, 255, 0.34)',
                        mx: 'auto',
                      }}
                    >
                      {(userProfile?.firstName?.[0] || userProfile?.username?.[0] || 'U').toUpperCase()}
                    </Avatar>
                    <IconButton
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        bgcolor: 'background.paper',
                        boxShadow: 2,
                        '&:hover': { bgcolor: 'background.paper' },
                      }}
                      size="small"
                    >
                      <EditIcon fontSize="small" color="primary" />
                    </IconButton>
                  </Box>
                  
                  <Typography variant="h5" fontWeight={700} gutterBottom>
                    {userProfile?.username || 'User'}
                  </Typography>
                  <Chip 
                    label={userProfile?.role || 'User'} 
                    color="primary" 
                    variant="outlined" 
                    size="small" 
                    icon={<AdminPanelSettingsIcon />}
                    sx={{ mb: 2 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {t('profile.memberSince', { year: new Date().getFullYear() })}
                  </Typography>
                </Paper>

                {/* Shortcuts Grid */}
                <Stack spacing={2}>
                  <ShortcutCard
                    to="/history"
                    icon={HistoryIcon}
                    title={t('profile.history')}
                    subtitle={t('profile.bidsAndPurchases')}
                    color="#6f7fff"
                  />
                  <ShortcutCard
                    to="/offers/my-offers"
                    icon={LocalOfferIcon}
                    title={t('profile.myOffers')}
                    subtitle={t('profile.secondHandOffers')}
                    color="#7657ff"
                  />
                  <ShortcutCard
                    to="/offers/received"
                    icon={InboxIcon}
                    title={t('profile.received')}
                    subtitle={t('profile.offersOnListings')}
                    color="#10b981"
                  />
                </Stack>
              </Stack>
            </Grid>

            {/* Right Column: Profile Details */}
            <Grid item xs={12} md={8}>
              <Paper
                sx={{
                  p: 4,
                  height: '100%',
                  background: theme.vitria?.input,
                  backdropFilter: 'blur(20px)',
                  borderRadius: borderRadius,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
                  <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon color="primary" /> {t('profile.personalInfo')}
                  </Typography>
                  {!isEditing ? (
                    <Button
                      variant="contained"
                      startIcon={<EditIcon />}
                      onClick={() => setIsEditing(true)}
                    >
                      {t('profile.editProfile')}
                    </Button>
                  ) : (
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        onClick={() => setIsEditing(false)}
                        color="error"
                      >
                        {t('cancel')}
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        color="success"
                      >
                        {t('save')}
                      </Button>
                    </Stack>
                  )}
                </Stack>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('profile.firstName')}
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      variant={isEditing ? "outlined" : "filled"}
                      InputProps={{
                        readOnly: !isEditing,
                        startAdornment: <BadgeIcon color="action" sx={{ mr: 1, opacity: 0.7 }} />,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('profile.lastName')}
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      variant={isEditing ? "outlined" : "filled"}
                      InputProps={{
                        readOnly: !isEditing,
                        startAdornment: <BadgeIcon color="action" sx={{ mr: 1, opacity: 0.7 }} />,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t('profile.username')}
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      variant={isEditing ? "outlined" : "filled"}
                      InputProps={{
                        readOnly: !isEditing,
                        startAdornment: <PersonIcon color="action" sx={{ mr: 1, opacity: 0.7 }} />,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t('profile.emailAddress')}
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      variant={isEditing ? "outlined" : "filled"}
                      InputProps={{
                        readOnly: !isEditing,
                        startAdornment: <EmailIcon color="action" sx={{ mr: 1, opacity: 0.7 }} />,
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('profile.role')}
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        bgcolor: 'rgba(255,255,255,0.02)',
                      }}
                    >
                      <AdminPanelSettingsIcon color="primary" />
                      <Typography fontWeight={600}>
                        {userProfile?.role || 'User'}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('profile.accountStatus')}
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        bgcolor: 'rgba(34, 197, 94, 0.05)',
                        borderColor: 'rgba(34, 197, 94, 0.2)',
                      }}
                    >
                      <VerifiedUserIcon color="success" />
                      <Typography fontWeight={600} color="success.main">
                        {userProfile?.status || t('profile.active')}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
          </Container>
        </Box>
      </Box>
    </SciFiBackground>
  );
};

export default Profile;
